import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import AxiosUtil from '../../../../Utils/AxiosUtil';
import parse from 'html-react-parser';
import {
	AgGrid,
	Alert as ServaAlert,
	Card,
	downloadCsv,
	downloadTabularExcel,
	IconBar,
	Modal as ServaModal,
	Skeleton,
} from '@servahealth/serva-ui-kit';
import useAuth from '../../../../Hooks/useAuth';
import DebugQuery from '../../../../Interfaces/Reports/DebugQuery';
import { ObjectUtil } from '../../../../Utils/ObjectUtil';
import ItemIdMap from '../../../../Interfaces/Reports/ItemIdMap';
import Reports from '../../../../Constants/Endpoints/Reports';
import ReportCustomizations from '../../../Home/ReportCustomizations';
import ReportItemNoData from '../ReportItemNoData';
import { populateDataSetInputs } from '../ReportItemHelper';
import GetDatasetDataUsingIDsV2 from '../../../../Interfaces/Requests/Reports/GetDatasetDataUsingIDsV2';
import DataSetDataResponseModel from '../../../../Interfaces/Responses/Reports/DataSets/DataSetDataResponseModel';
import DataSetColumnFormatModel from '../../../../Interfaces/Responses/Reports/DataSets/DatasetColumnFormatModel';
import AgGridJSRowData from '../../../../Interfaces/Responses/Reports/DataSets/DataSetData';
import {dateTimeComparator, isDateColumnFormat, transformServaGridToAgGridJSGrid as transformServaGridDataToAgGridJSData,} from '../../../../Utils/AgGridUtil';

interface DataSetItemProps extends ItemIdMap {
	handleRowClick: (
		dataSetIndex: number,
		index: number,
		rowData: any,
		gridData: AgGridJSRowData
	) => void;
}

const DataSetItem = ({ handleRowClick, ...item }: DataSetItemProps) => {
	const { user } = useAuth();
	const [debugQuery, setDebugQuery] = useState<DebugQuery>();
	const [debugQueryVisible, setDebugQueryVisible] = useState(false);
	const [dataSet, setDataSet] = useState<any>(null);
	const [gridData, setGridData] = useState<AgGridJSRowData>();
	const [reportCustomizationVisible, setReportCustomizationVisible] = useState(false);
	const [dataSetColumnFormats, setDataSetColumnFormats] = useState<DataSetColumnFormatModel[]>([]);
	const [customReportName, setCustomReportName] = useState('');

	const [currentError, setCurrentError] = useState('');
	
	const dataSetParameters = item.dataSetParameters;

	const hasAppliedFilters = item.filterInputs?.some((filterInput) => filterInput.hasValue) ?? false;

	const { isLoading: isDataSetLoading, isError: isDataSetLoadingError, error: getDataSetLoadingError } = useQuery(
		['dataset', item.dataSetParameters?.dataSetId, item.filterInputs],
		() => {
			try {
				if (dataSetParameters) {
					const url = `${process.env.REACT_APP_API_ROOT}${Reports.GET_DATASET_DATA_USING_IDS_V2}`;
					const inputs = populateDataSetInputs(
						item.dataSetParameters,
						item.inputMaps,
						item.filterInputs
					);
					const requestData: GetDatasetDataUsingIDsV2 = {
						currentToken: user?.runningUser.currentToken ?? '',
						datasetID: dataSetParameters?.dataSetId ?? -1,
						tenantID: dataSetParameters?.tenantId.toString() ?? '',
						reportItemID: dataSetParameters.reportItemId,
						inputs: inputs,
					};
					const response = AxiosUtil.post<DataSetDataResponseModel, GetDatasetDataUsingIDsV2>(url, requestData);
					return response;
				}
            } catch (err) {
                setCurrentError('GET_DATASET_DATA_USING_IDS_V2 [Load Dataset] Internal Error: ' + err);
                console.error('GET_DATASET_DATA_USING_IDS_V2 [Load Dataset] Internal Error: ', err);
                return Promise.reject();
            }
		},
		{
            onError: (err: any) => {
                setCurrentError('GET_DATASET_DATA_USING_IDS_V2 [Load Dataset] Internal Error: ' + err);
                console.error('GET_DATASET_DATA_USING_IDS_V2 [Load Dataset] Internal Error: ', err);
			},
			onSuccess: (response: DataSetDataResponseModel) => {
				// console.log('GET_DATASET_DATA_USING_IDS_V2 [ReportItem/DatatSetItem/index]');
				// ---- DEBUG
				// console.log(response);
				// ---- END DEBUG

				if (!dataSetParameters) return {};

				const gridData = transformServaGridDataToAgGridJSData(response, response?.name ?? '');

				setGridData(gridData);

				setDebugQuery({
					queryReportId: dataSetParameters.reportId,
					queryReportName: response.name,
					queryReportPageId: dataSetParameters.reportPageId,
					queryReportItemId: dataSetParameters.reportItemId,
					queryChartId: 0,
					queryDataSetId: dataSetParameters.dataSetId,
					queryString: response.queryDebug,
				});

				const {	columns, data, drilldowns, columnDefinitions, datasetColumnFormats } = response;

				// ---- DEBUG
				// console.log('**** columns ****');
				// console.log(columns);
				// console.log('**** data ****');
				// console.log(data);
				// console.log('**** columnDefinitions ****');
				// console.log(columnDefinitions);
				// console.log('**** datasetColumnFormats ****');
				// console.log(datasetColumnFormats);
				// ---- END DEBUG

				setCustomReportName(response.name);

				if (datasetColumnFormats.length !== 0) {
					setDataSetColumnFormats(datasetColumnFormats);
				}

				const hiddenTargets = columnDefinitions.filter((column: any) => !column.visible).map((column: any) => column.targets);

				hiddenTargets.forEach((index: number) => {
					if (columns[index]) {
						columns[index].hidden = true;
					}
				});

				/*const rowData = data.map((rowData: any) => {
					const rowDataObj: { [key: string]: any } = {};
					columns.forEach((column: any, index: number) => {
						rowDataObj[column.title] = ObjectUtil.IsEmptyObject(
							rowData[index]
						)
							? ''
							: rowData[index];
					});
					return rowDataObj;
				});*/

				const rowData = data.map((gridData: any) => {
					const rowDataObj: { [key: string]: any } = {};
					columns.forEach((column: any, index: number) => {
						rowDataObj[column.title] = ObjectUtil.IsEmptyObject(gridData[index]) ? '' : gridData[index];
					});
					return rowDataObj;
				});

				const keys = !!rowData && rowData[0] && Object.keys(rowData[0]);
				const pinnedBottomRowData = !!keys ? response.datasetAggregates?.reduce(
					(acc: any, item: any) => {
						const keyIndex = item.columnField;
						if (keys[keyIndex]) {
							acc[keys[keyIndex]] = item.valueField;
						}
						return acc;
					},
					{}
				) : undefined;

				// ---- DEBUG
				// console.log('**** datasetAggregates ****');
				// console.log(datasetAggregates);
				// console.log('**** keys ****');
				// console.log(keys);
				// console.log('**** pinnedBottomRowData ****');
				// console.log(pinnedBottomRowData);
				// ---- END DEBUG

				setDataSet({
					columnDefs: columns.map((column: any) => ({
						autoHeaderHeight: true,
						cellStyle: {textAlign: column.alignment},
						comparator: isDateColumnFormat(column.name, datasetColumnFormats) ? dateTimeComparator : undefined,
						field: column.title,
						filter: true,
						headerName: column.title,
						hide: column.hidden,
						sortable: true,
						wrapHeaderText: true,
						cellRenderer: datasetColumnFormats.some((x: any) => x.columnName === column.name && x.allowHtml) ? (params: any) => parse(params.value ?? '') : undefined,
					})),
					drilldowns: drilldowns,
					enableValue: true,
					id: dataSetParameters.reportItemId,
					pinnedBottomRowData: !!pinnedBottomRowData && Object.keys(pinnedBottomRowData).length === 0 ? undefined : [pinnedBottomRowData],
					rowData: rowData,
					singleDataPointValue: response.singleDataPointResult,
					type: response.singleDataPoint ? 'singleDataPoint' : 'grid',
				});
			},
			refetchOnWindowFocus: false,
			refetchOnReconnect: false,
		}
	);

	const statusBar = useMemo(() => {
		return {
			statusPanels: [
				{ statusPanel: "agTotalAndFilteredRowCountComponent" },
				{ statusPanel: "agTotalRowCountComponent" },
				{ statusPanel: "agFilteredRowCountComponent" },
				{ statusPanel: "agSelectedRowCountComponent" },
				{ statusPanel: 'agAggregationComponent' }
			],
		};
	}, []);
	  
	const iconBarArgsAdmin: any = {
		icons: [
			{
				type: 'csv',
				tooltipText: 'download csv',
				onClick: () =>
					downloadCsv (
						dataSet?.columnDefs ?? [],
						dataSet?.rowData ?? [],
						(dataSetParameters?.reportName ?? 'rpt_csv') + '.csv'
					),
			},
			{
				type: 'excel',
				tooltipText: 'download excel',
				onClick: () =>
					downloadTabularExcel (
						dataSet?.columnDefs ?? [],
						dataSet?.rowData ?? [],
						(dataSetParameters?.reportName ?? 'rpt_excel') + '.xlsx'
					),
			},
			{
				type: 'edit',
				tooltipText: (dataSetColumnFormats.length > 0 ? 'customizations available' : 'no customizations available'),
				onClick: (dataSetColumnFormats.length > 0 ? (() => setReportCustomizationVisible(true)) : (undefined)),
			},
			{
				type: 'debug',
				tooltipText: 'report debug',
				onClick: () => setDebugQueryVisible(true),
			},
		],
		orientation: 'horizontal',
		spacing: 30,
	};
	const iconBarArgs: any = {
		icons: [
			{
				type: 'csv',
				tooltipText: 'download csv',
				onClick: () =>
					downloadCsv (
						dataSet?.columnDefs ?? [],
						dataSet?.rowData ?? [],
						(dataSetParameters?.reportName ?? 'rpt_csv') + '.csv'
					),
			},
			{
				type: 'excel',
				tooltipText: 'download excel',
				onClick: () =>
					downloadTabularExcel (
						dataSet?.columnDefs ?? [],
						dataSet?.rowData ?? [],
						(dataSetParameters?.reportName ?? 'rpt_excel') + '.xlsx'
					),
			},
			{
				type: 'edit',
				tooltipText: 'customize report',
				onClick: (dataSetColumnFormats.length > 0 ? (() => setReportCustomizationVisible(true)) : (undefined)),
			},
		],
		orientation: 'horizontal',
		spacing: 30,
	};

    const determineWhichError = () => {
        if ( isDataSetLoadingError ) {
            return currentError + ' ' + (getDataSetLoadingError as any).response?.data.message;
        }
        else {
            return '';
        }
    } 

	if (isDataSetLoadingError)
		return (
			<ServaAlert
				className={'mb-2'}
				message={determineWhichError()}
				showIcon={true}
				type="error"
			/>
		);

	if (!dataSet || isDataSetLoading) {
		return <Skeleton active />;
	}
	else if ((dataSet?.type === 'singleDataPoint') && (!dataSet?.singleDataPointValue)) {
		return (
			<div
				className="report-item"
				style={{
					height: item.fullPage ? '95%' : 'inherit',
					position: 'relative',
				}}
			>
				<div
					style={{
						border: '1px solid #000080',
						height: '25px',
					}}
				>
					<div
						style={{
							fontWeight: 'bold',
							textAlign: 'center',
							width: '100%',
							height: '25px',
							backgroundColor: '#B0C4DE',
							color: '#000080',
						}}
					>
						<span>{customReportName}</span>
					</div>
				</div>
				<ReportItemNoData
					debugQuery={debugQuery}
					debugQueryVisible={debugQueryVisible}
					hasFilters={hasAppliedFilters}
					showDebug={user?.runningUser.isSecurityAdmin}
					onCloseDebug={() => setDebugQueryVisible(false)}
				/>
			</div>
		);
	}
	else if ((dataSet?.type !== 'singleDataPoint') && (!dataSet?.rowData || !dataSet?.rowData[0])) {
		return (
			<Card
				className="shadow-card"
				size="small"
				title={customReportName}
			>
				<div
					className="report-item"
					style={{
						height: item.fullPage ? '95%' : 'inherit',
						position: 'relative',
					}}
				>
					<ReportItemNoData
						debugQuery={debugQuery}
						debugQueryVisible={debugQueryVisible}
						hasFilters={hasAppliedFilters}
						showDebug={user?.runningUser.isSecurityAdmin}
						onCloseDebug={() => setDebugQueryVisible(false)}
					/>
				</div>
			</Card>
		);
	}
	else {
		return (
			(dataSet?.type && (dataSet?.type !== 'singleDataPoint')) ?
			(
				<div className="report-item-renderer-frame">
					<ServaModal
						destroyOnHidden={true}
						hideButtons={true}
						open={reportCustomizationVisible}
						width={'1000px'}
						onCancel={() => setReportCustomizationVisible(false)}
					>
						<ReportCustomizations
							currentToken={user?.runningUser.currentToken}
							dataSetColumnFormatModels={dataSetColumnFormats}
							name={customReportName}
							reportId={dataSetParameters?.reportItemId ?? 0}
							reportItemId={dataSetParameters?.reportItemId ?? 0}
							close={() => setReportCustomizationVisible(false)}
						></ReportCustomizations>
					</ServaModal>
					<ReportItemNoData
						debugOnly={true}
						debugQuery={debugQuery}
						debugQueryVisible={debugQueryVisible}
						hasFilters={hasAppliedFilters}
						showDebug={user?.runningUser.isSecurityAdmin}
						onCloseDebug={() => setDebugQueryVisible(false)}
					/>
					<Card
						className={(dataSet?.drilldowns?.length ?? 0) > 0 ? 'shadow-card-drilldown' : 'shadow-card'}
						extra={(
							user?.runningUser.isSecurityAdmin ?
								<div><IconBar size="small" {...iconBarArgsAdmin} /></div> : 
								<div><IconBar size="small" {...iconBarArgs} /></div>
						)}
						size="small"
						title={customReportName}
					>
						<div
							className="report-item"
							style={{
								height: '100%',
								minHeight: 0,
								position: 'relative',
							}}
						>
							<div style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
								<AgGrid
									columnDefs={dataSet?.columnDefs}
									fullPage={item.fullPage}
									gridData={gridData}
									pinnedBottomRowData={dataSet?.pinnedBottomRowData}
									rowData={dataSet?.rowData}
									statusBar={statusBar}
									onDataSetRowClick={ handleRowClick }
								/>
							</div>
						</div>
					</Card>
				</div>
			)
			:
			(
				<div>
					<label>
						{!!dataSet?.singleDataPointValue ? parse(dataSet?.singleDataPointValue) : null}
					</label>
				</div>
			)
		);
	}
};

export default DataSetItem;
